import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import RoleFormModal from '../components/RoleFormModal';
import './RoleManagement.css';
import { toast } from '../components/Toast';

function RoleManagement() {
  const [roles, setRoles] = useState([]);
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  useEffect(() => {
    fetchRoles();
    fetchMenus();
  }, []);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/roles');
      setRoles(response.data.data || response.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to fetch roles');
    } finally {
      setLoading(false);
    }
  };

  const fetchMenus = async () => {
    try {
      const response = await axios.get('/menus');
      setMenus(response.data.data || response.data);
    } catch (err) {
      console.error('Failed to fetch menus:', err);
    }
  };

  const handleCreate = () => {
    setSelectedRole(null);
    setShowModal(true);
  };

  const handleEdit = (role) => {
    setSelectedRole(role);
    setShowModal(true);
  };

  const handleDelete = async (role) => {
    if (['Company Admin', 'Branch Manager'].includes(role.name)) {
      toast.error('Critical roles cannot be deleted');
      return;
    }
    if (!window.confirm(`Delete role "${role.name}"?`)) return;
    try {
      await axios.delete(`/roles/${role.id}`);
      fetchRoles();
      flash('Role deleted successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete role');
    }
  };

  const flash = (msg) => {
    toast.success(msg);
  };

  return (
    <div className="rm-container">
      <div className="rm-header">
        <div>
          <h1 className="rm-title">Roles &amp; Permissions</h1>
          <p className="rm-subtitle">Create roles and assign menu access for your team</p>
        </div>
        <button className="rm-btn-primary" onClick={handleCreate}>
          + Create Role
        </button>
      </div>

      {loading ? (
        <div className="rm-loading">Loading roles...</div>
      ) : roles.length === 0 ? (
        <div className="rm-empty">
          <div className="rm-empty-icon">🔐</div>
          <p>No roles yet. Create your first role to get started.</p>
        </div>
      ) : (
        <div className="rm-grid">
          {roles.map((role) => (
            <div key={role.id} className="rm-card">
              <div className="rm-card-top">
                <div className="rm-card-title-row">
                  <span className="rm-role-name">{role.name}</span>
                  <div className="rm-badges">
                    {role.isSystemRole && <span className="rm-badge rm-badge-system">System</span>}
                    <span className={`rm-badge ${role.active ? 'rm-badge-active' : 'rm-badge-inactive'}`}>
                      {role.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                <p className="rm-role-desc">{role.description || 'No description'}</p>
              </div>

              <div className="rm-card-menus">
                <span className="rm-menus-label">Menus</span>
                {role.menus && role.menus.length > 0 ? (
                  <div className="rm-menu-chips">
                    {role.menus.map(m => (
                      <span key={m.id} className="rm-chip">{m.label}</span>
                    ))}
                  </div>
                ) : (
                  <span className="rm-no-menus">No menus assigned</span>
                )}
              </div>

              <div className="rm-card-actions">
                <button className="rm-btn-secondary" onClick={() => handleEdit(role)}>
                  Edit
                </button>
                {!['Company Admin', 'Branch Manager'].includes(role.name) && (
                  <button className="rm-btn-danger" onClick={() => handleDelete(role)}>
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <RoleFormModal
          role={selectedRole}
          menus={menus}
          onClose={() => { setShowModal(false); setSelectedRole(null); }}
          onSuccess={() => { setShowModal(false); setSelectedRole(null); fetchRoles(); flash('Role saved successfully'); }}
        />
      )}
    </div>
  );
}

export default RoleManagement;
